import {useEffect, useState, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";

import {ContractAPI} from "@/api/contract";

import AddIcon from "@mui/icons-material/Add";

import {dateFormat, ERROR_OPERATION} from "@/utils/constants";

import "./index.scss";

const CONTRACT_TYPE_LABELS = {
	professional: "Professionistico",
	amateur: "Dilettantistico",
	youth: "Settore giovanile",
	loan: "Prestito",
	amateur_professional: "Semiprofessionistico",
};

const STATUS_LABELS = {
	active: "Attivo",
	expired: "Scaduto",
	terminated: "Risolto",
	suspended: "Sospeso",
};

const STATUS_CLS = {
	active: "active",
	expired: "expired",
	terminated: "terminated",
	suspended: "suspended",
};

const DAYS_OPTIONS = [
	{value: 30, label: "30 giorni"},
	{value: 60, label: "60 giorni"},
	{value: 90, label: "90 giorni"},
	{value: 180, label: "6 mesi"},
	{value: 365, label: "1 anno"},
];

const DAYS_LABEL_MAP = {
	30: "30 giorni",
	60: "60 giorni",
	90: "90 giorni",
	180: "6 mesi",
	365: "1 anno",
};

const formatSalary = (value) => {
	if (value == null) return "—";
	return (
		"€ " +
		Number(value).toLocaleString("it-IT", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
	);
};

const getUrgency = (days) => {
	if (days === null || days === undefined) return "none";
	if (days < 0) return "expired";
	if (days <= 30) return "critical";
	if (days <= 90) return "warning";
	return "safe";
};

// ── Contract card ─────────────────────────────────────────────────────────────

function ContractCard({contract, onClick, onPlayerClick}) {
	const urgency = getUrgency(contract.daysToExpiry);
	const statusCls = STATUS_CLS[contract.status] || "active";

	return (
		<div
			className={"ct-card ct-card--" + urgency}
			onClick={() => onClick(contract)}
		>
			<div className="ct-card__top">
				<div className="ct-card__player-block">
					<span
						className="ct-card__player-name"
						onClick={(e) => {
							e.stopPropagation();
							onPlayerClick(contract.playerId);
						}}
					>
						{contract.playerLastName} {contract.playerFirstName}
					</span>
					<span className="ct-card__type">
						{CONTRACT_TYPE_LABELS[contract.contractType] ||
							contract.contractType ||
							"—"}
					</span>
				</div>
				{contract.status && (
					<span className={"ct-card__status ct-card__status--" + statusCls}>
						{STATUS_LABELS[contract.status] || contract.status}
					</span>
				)}
			</div>

			<div className="ct-card__metrics">
				<div className="ct-card__metric">
					<span className="ct-card__metric-label">Scadenza</span>
					<span className="ct-card__metric-value">
						{contract.endDate
							? moment(contract.endDate).format(dateFormat)
							: "—"}
					</span>
				</div>
				<div className="ct-card__metric">
					<span className="ct-card__metric-label">Giorni</span>
					<span
						className={
							"ct-card__metric-value ct-card__metric-value--" + urgency
						}
					>
						{contract.daysToExpiry != null
							? contract.daysToExpiry < 0
								? "Scaduto"
								: contract.daysToExpiry + " gg"
							: "—"}
					</span>
				</div>
				<div className="ct-card__metric">
					<span className="ct-card__metric-label">Stipendio</span>
					<span className="ct-card__metric-value">
						{formatSalary(contract.totalMonthlySalary)}
					</span>
				</div>
			</div>
		</div>
	);
}

// ── KPI strip ─────────────────────────────────────────────────────────────────

function KPIStrip({contracts}) {
	const stats = useMemo(() => {
		const total = contracts.length;
		const critical = contracts.filter(
			(c) =>
				c.daysToExpiry != null && c.daysToExpiry >= 0 && c.daysToExpiry <= 30,
		).length;
		const warning = contracts.filter(
			(c) =>
				c.daysToExpiry != null && c.daysToExpiry > 30 && c.daysToExpiry <= 90,
		).length;
		const expired = contracts.filter(
			(c) => c.daysToExpiry != null && c.daysToExpiry < 0,
		).length;
		const totalSalary = contracts.reduce(
			(sum, c) => sum + (c.totalMonthlySalary || 0),
			0,
		);
		return {total, critical, warning, expired, totalSalary};
	}, [contracts]);

	return (
		<div className="ct-kpi">
			<div className="ct-kpi__item">
				<span className="ct-kpi__value">{stats.total}</span>
				<span className="ct-kpi__label">Contratti</span>
			</div>
			{stats.critical > 0 && (
				<div className="ct-kpi__item ct-kpi__item--critical">
					<span className="ct-kpi__value">{stats.critical}</span>
					<span className="ct-kpi__label">{"< 30 gg"}</span>
				</div>
			)}
			{stats.warning > 0 && (
				<div className="ct-kpi__item ct-kpi__item--warning">
					<span className="ct-kpi__value">{stats.warning}</span>
					<span className="ct-kpi__label">{"< 90 gg"}</span>
				</div>
			)}
			{stats.expired > 0 && (
				<div className="ct-kpi__item ct-kpi__item--expired">
					<span className="ct-kpi__value">{stats.expired}</span>
					<span className="ct-kpi__label">Scaduti</span>
				</div>
			)}
			<div className="ct-kpi__item">
				<span className="ct-kpi__value">{formatSalary(stats.totalSalary)}</span>
				<span className="ct-kpi__label">Monte stipendi</span>
			</div>
		</div>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

function ContractsPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [contracts, setContracts] = useState([]);
	const [search, setSearch] = useState("");
	const [daysFilter, setDaysFilter] = useState(
		JSON.parse(sessionStorage.getItem("contracts_days_filter")) || null,
	);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const response = await ContractAPI.expiring(daysFilter || 36500);
				setContracts(response || []);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [daysFilter]);

	const filtered = useMemo(() => {
		let items = contracts;
		if (search) {
			const q = search.toLowerCase();
			items = items.filter((c) =>
				(c.playerLastName + " " + c.playerFirstName).toLowerCase().includes(q),
			);
		}
		return [...items].sort((a, b) =>
			(a.playerLastName || "").localeCompare(b.playerLastName || ""),
		);
	}, [contracts, search]);

	const handleDaysChange = (value) => {
		const v = value?.value || null;
		sessionStorage.setItem("contracts_days_filter", JSON.stringify(v));
		setDaysFilter(v);
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			title: "Nuovo contratto",
			onClick: () => navigate("/manager/players-contracts/new"),
		},
	];

	return (
		<Page className="ct-page">
			<Loading visible={loading} />
			<Breadcrumbs
				current="Contratti"
				actions={<MainActions actions={mainActions} />}
			/>

			{/* KPI strip */}
			{contracts.length > 0 && <KPIStrip contracts={contracts} />}

			{/* Filtri */}
			<div className="ct-filters">
				<div className="ct-filters__field">
					<VLFTextField
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Cerca giocatore…"
						size="small"
					/>
				</div>
				<div className="ct-filters__select">
					<VLFSelectNew
						options={DAYS_OPTIONS}
						sorted={false}
						value={
							daysFilter
								? {
										value: daysFilter,
										label: DAYS_LABEL_MAP[daysFilter] || daysFilter + " giorni",
									}
								: null
						}
						onChange={handleDaysChange}
						label="Scadenza entro"
						isClearable
					/>
				</div>
			</div>

			{/* Lista card */}
			<div className="ct-list">
				{filtered.length > 0 ? (
					filtered.map((contract) => (
						<ContractCard
							key={contract.contractId || contract.id}
							contract={contract}
							onClick={(c) =>
								navigate("/manager/players-contracts/" + (c.contractId || c.id))
							}
							onPlayerClick={(playerId) => navigate("/players/" + playerId)}
						/>
					))
				) : (
					<div className="ct-empty">
						{search
							? "Nessun contratto trovato per la ricerca."
							: "Nessun contratto presente."}
					</div>
				)}
			</div>
		</Page>
	);
}

export default ContractsPage;
